import type { Alpine as AlpineAPI } from 'alpinejs';
import markup from '../../modules/single-select-control.template.html' with { type: 'text' };

const enhanced = new WeakSet<HTMLSelectElement>();
type Option = { index: number; label: string; group: string; disabled: boolean };

function eligible(select: HTMLSelectElement) {
    if (select.multiple || select.size > 1 || select.matches('.select-multi, [hidden], [data-searchable="false"]') || select.closest('.cke, .datepicker')) return false;
    if (select.dataset.searchable === 'true') return true;
    if (select.options.length < 12 || /dob_|birth|cc_(month|year)|club.?year/i.test(select.name + select.id)) return false;
    const options = [...select.options].filter(option => option.value);
    return options.some(option => !/^\d+(?:\/\d+)?$/.test(option.text.trim()));
}

/** Native selects remain the source of truth for PHP, Alpine models, and existing change handlers. */
export function registerSingleSelect(Alpine: AlpineAPI) {
    Alpine.data('nativeSearchSelect', () => {
        let observer: MutationObserver | undefined;
        let dispose = () => {};
        return {
            native: null as HTMLSelectElement | null,
            options: [] as Option[],
            selected: -1,
            title: 'option',
            searchTerm: '',
            isOpen: false,
            disabled: false,
            invalid: false,
            position: {} as Record<string, string>,
            init() {
                const select = this.$el.previousElementSibling;
                if (!(select instanceof HTMLSelectElement)) return;
                this.native = select;
                const label = select.labels?.[0];
                // Read only the label's own text, excluding its option list and generated controls.
                this.title = select.getAttribute('aria-label') || (label ? [...label.childNodes].filter(node => node.nodeType === Node.TEXT_NODE).map(node => node.textContent).join(' ').trim() : '') || select.dataset.label || 'option';
                const refresh = () => this.refresh();
                const reset = () => setTimeout(refresh, 0);
                const invalid = (event: Event) => { event.preventDefault(); this.refresh(); this.invalid = true; this.$nextTick(() => this.$refs.trigger.focus()); };
                const move = (event: Event) => {
                    if (this.isOpen && (!(event.target instanceof Node) || !this.$refs.panel.contains(event.target))) this.positionPanel();
                };
                const otherOpen = (event: Event) => { if ((event as CustomEvent).detail !== this.$el) this.close(); };
                const labelClick = (event: MouseEvent) => {
                    if (event.target instanceof Element && event.target.closest('button, input, select, a, [data-native-picker]')) return;
                    event.preventDefault(); this.$nextTick(() => this.open());
                };
                select.addEventListener('input', refresh);
                select.addEventListener('change', refresh);
                select.addEventListener('invalid', invalid);
                select.form?.addEventListener('reset', reset);
                label?.addEventListener('click', labelClick);
                window.addEventListener('resize', move);
                document.addEventListener('scroll', move, true);
                document.addEventListener('ab4h:select-open', otherOpen);
                observer = new MutationObserver(refresh);
                observer.observe(select, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'selected', 'label', 'value', 'required'] });
                dispose = () => {
                    select.removeEventListener('input', refresh); select.removeEventListener('change', refresh);
                    select.removeEventListener('invalid', invalid); select.form?.removeEventListener('reset', reset);
                    label?.removeEventListener('click', labelClick); window.removeEventListener('resize', move);
                    document.removeEventListener('scroll', move, true); document.removeEventListener('ab4h:select-open', otherOpen);
                };
                this.refresh();
            },
            destroy() { observer?.disconnect(); dispose(); },
            refresh() {
                if (!this.native) return;
                this.options = [...this.native.options].map((option, index) => ({
                    index, label: option.text.trim(),
                    group: option.parentElement instanceof HTMLOptGroupElement ? option.parentElement.label : '',
                    disabled: option.disabled || (option.parentElement instanceof HTMLOptGroupElement && option.parentElement.disabled),
                }));
                this.selected = this.native.selectedIndex;
                this.disabled = this.native.matches(':disabled');
                if (this.native.validity.valid) this.invalid = false;
                if (this.disabled) this.close();
            },
            get summary() { return this.options.find(option => option.index === this.selected)?.label || 'Select an option…'; },
            filtered(): Option[] {
                const term = this.searchTerm.trim().toLocaleLowerCase();
                return this.options.filter(option => !term || `${option.label} ${option.group}`.toLocaleLowerCase().includes(term));
            },
            groups(): Record<string, Option[]> {
                const groups: Record<string, Option[]> = Object.create(null);
                for (const option of this.filtered()) (groups[option.group] ||= []).push(option);
                return groups;
            },
            positionPanel() {
                const rect = this.$refs.trigger.getBoundingClientRect();
                const above = rect.top > innerHeight - rect.bottom && innerHeight - rect.bottom < 280;
                const width = Math.min(innerWidth - 16, Math.max(rect.width, 240));
                this.position = {
                    left: `${Math.max(8, Math.min(rect.left, innerWidth - width - 8))}px`, right: 'auto', width: `${width}px`,
                    top: above ? 'auto' : `${rect.bottom + 4}px`, bottom: above ? `${innerHeight - rect.top + 4}px` : 'auto',
                    maxHeight: `${Math.max(100, Math.min(360, (above ? rect.top : innerHeight - rect.bottom) - 12))}px`,
                };
            },
            open() {
                this.refresh();
                if (this.disabled) return;
                document.dispatchEvent(new CustomEvent('ab4h:select-open', { detail: this.$el }));
                this.positionPanel();
                this.searchTerm = '';
                this.isOpen = true;
                this.$nextTick(() => requestAnimationFrame(() => {
                    if (!this.isOpen) return;
                    this.$refs.panel.showPopover();
                    this.$refs.search.focus({ preventScroll: true });
                }));
            },
            close(returnFocus = false) {
                if (!this.isOpen) return;
                this.$refs.panel.hidePopover();
                this.isOpen = false;
                if (returnFocus) this.$refs.trigger.focus({ preventScroll: true });
            },
            toggle() { if (this.isOpen) this.close(); else this.open(); },
            choose(index: number) {
                if (!this.native || this.disabled || this.options.find(option => option.index === index)?.disabled) return;
                this.native.selectedIndex = index;
                this.native.dispatchEvent(new Event('input', { bubbles: true }));
                this.native.dispatchEvent(new Event('change', { bubbles: true }));
                this.refresh(); this.close(true);
            },
            focusOption(index: number) { this.$refs.options.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')[index]?.focus(); },
            moveOption(direction: number) {
                const options = [...this.$refs.options.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
                const index = options.indexOf(document.activeElement as HTMLButtonElement);
                options[(index + direction + options.length) % options.length]?.focus();
            },
        };
    });
}

export function enhanceSingleSelects(root: ParentNode) {
    const parent = root instanceof Element ? root.closest('select') : null;
    const selects = [...(parent instanceof HTMLSelectElement ? [parent] : []), ...root.querySelectorAll<HTMLSelectElement>('select')];
    for (const select of selects) {
        if (enhanced.has(select) || !eligible(select)) continue;
        enhanced.add(select);
        select.hidden = true;
        select.dataset.searchableNative = '';
        const wrapper = document.createElement('div');
        wrapper.dataset.nativePicker = '';
        wrapper.className = 'relative min-w-0 w-full mb-4';
        if ([...select.classList].some(name => /^(?:mb-0!?|mb-\[0em\])$/.test(name))) wrapper.classList.replace('mb-4', 'mb-0');
        wrapper.setAttribute('x-data', 'nativeSearchSelect');
        const model = select.getAttribute('x-model');
        if (model) wrapper.setAttribute('x-effect', `${model}; $nextTick(() => refresh())`);
        const visibility = select.getAttribute('x-show');
        if (visibility) wrapper.setAttribute('x-show', visibility);
        wrapper.innerHTML = markup;
        select.after(wrapper);
    }
}

/** Call on detached clones before inserting them, so each select gets a fresh Alpine controller. */
export function resetSelectClones(root: ParentNode) {
    root.querySelectorAll('[data-native-picker]').forEach(picker => picker.remove());
    root.querySelectorAll<HTMLSelectElement>('[data-searchable-native]').forEach(select => {
        select.hidden = false;
        delete select.dataset.searchableNative;
    });
}
